import { baseUrl } from './general-api';
import { HamiltonImage } from './products-api';

/**
 * Base URL for the menu API for the e-catalog.
 */
export const baseMenuPath = baseUrl + '/.rest/delivery/v1/catalog/menu';

/**
 * Base URL for the menu API for the resources center.
 */
export const baseMenuPathResources = baseUrl + '/.rest/delivery/v1/resources/menu';

/**
 * Base URL for the menu API for the news archive.
 */
export const baseMenuPathNews = baseUrl + '/.rest/delivery/v1/news/menu';

/**
 * Header menu items that are displayed in the first row of the header.
 * These are represented by 'taxonomy categories'.
 */
export interface FirstLevelMenuItem {
  /**
   * Translated name that is displayed in the UI.
   */
  name: string;
  /**
   * Translated label that is displayed in the UI.
   */
  label: string;
  /**
   * Taxonomy category ID.
   */
  id: string;
  /**
   * The image that is shown on the overview page.
   */
  image: HamiltonImage;
  /**
   * The description that is shown on the overview page.
   */
  description: string;
  /**
   * List of second level menu items that are displayed when this
   * menu item was selected by the user.
   */
  secondaryCategories: SecondLevelMenuItem[];
}

/**
 * Header menu items that are displayed in the second row of the header.
 * These are represented by 'taxonomies'.
 */
export interface SecondLevelMenuItem {
  /**
   * Translated name that is displayed in the UI.
   */
  name: string;
  /**
   * Taxonomy ID.
   */
  id: string;
  /**
   * List of third and fourth level items. These are the
   * IDs (strings) of the taxonomy categories that should
   * be displayed in the filter. With this category ID
   * the name of the filter group can be retrieved from
   * the taxonomies API and the filter options in this
   * group can be looked up from the taxonomies API
   * under the respective ID.
   */
  filters: string[];
}

/**
 * Different Pages (E-Catalog, Resources Center and News Archive) have different menus.
 */
export enum MenuType {
  MENU_E_CATALOG,
  MENU_RESOURCES,
  MENU_NEWS
}

/**
 * Returns all first and second menu items as well as the list of filters (taxonomy category IDs) for each second level item.
 */
export const getMenuItems = (menuType?: MenuType): Promise<FirstLevelMenuItem[]> => {
  let menuPath = '';

  switch (menuType) {
    case MenuType.MENU_E_CATALOG:
      menuPath = baseMenuPath;
      break;
    case MenuType.MENU_RESOURCES:
      menuPath = baseMenuPathResources;
      break;
    case MenuType.MENU_NEWS:
      menuPath = baseMenuPathNews;
      break;
  }

  return fetch(menuPath)
    .then((response) => response.json())
    .then((data: FirstLevelMenuItem[]) => {
      if (data) {
        // remove any second level items, that are named 'none', they are
        // only provided by the API, because there apparently must be a second level item
        // but we do not want to display them in the UI, so we remove them
        // an exception is the only second level item for the "news" page, this must be included
        for (const category of data) {
          const subCategories = [];
          for (const subCategory of category.secondaryCategories) {
            if (subCategory.name !== 'none' || category.name === 'News') {
              subCategories.push(subCategory);
            }
          }
          category.secondaryCategories = subCategories;
        }

        return data;
      }

      return [];
    })
    .catch(() => {
      return [];
    });
};
